import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { http } from '../lib/http'
import { CmsContext } from './cms-context'
import type { CmsEditorMessage, CmsLayout, CmsPreviewMessage } from './types'

/* The storefront's content, and — when the console has this page in a frame —
 * the live wire between the two.
 *
 * The wire is postMessage rather than a saved draft on the server, and that
 * is the design decision this whole feature turns on. A draft would mean the
 * console saving on every keystroke, a second copy of every page in the
 * database, and a publish step to explain. postMessage means the editor holds
 * the unsaved page in its own state and hands it to the frame as it is typed:
 * the preview is genuinely live, nothing is written until Save, and closing
 * the tab throws the draft away exactly the way an unsaved document should.
 *
 * Outside the frame none of it runs. `preview` is false, no listener is
 * attached, and the page is the ordinary public site reading its copy from
 * one cached request.
 */

/** True when this page is the console's preview frame. Both halves have to
    agree: the flag says the console meant this, and being framed says it
    really is inside something. A visitor who types ?cms=1 gets the ordinary
    site, because there is no parent to talk to. */
function isPreviewFrame() {
  if (typeof window === 'undefined') return false
  const framed = window.parent && window.parent !== window
  return Boolean(framed) && new URLSearchParams(window.location.search).get('cms') === '1'
}

const fetchLayout = async (page: string) => {
  const { data } = await http.get<CmsLayout>(`/cms/pages/${page}`)
  return data
}

/* The last page this browser was served, kept so the order paints correctly
   on the first frame of a return visit.
 *
 * Without it the home page has a real problem: the running order arrives one
 * request after the markup does, so a visitor watches the sections shuffle
 * into place. Seeding React Query from the last known good answer means a
 * returning visitor sees the right page immediately and the request behind it
 * only ever confirms it. A first-ever visit still renders — the built-in
 * order below — it just may reorder once.
 *
 * Every accessor is wrapped: a private window, cleared site data or a browser
 * set to refuse storage must leave the site working, not throw on boot. */
const cacheKey = (page: string) => `sm28-cms-${page}`

function readCache(page: string): CmsLayout | undefined {
  try {
    const raw = window.localStorage.getItem(cacheKey(page))
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as CmsLayout
    return Array.isArray(parsed?.sections) ? parsed : undefined
  } catch {
    return undefined
  }
}

function writeCache(page: string, layout: CmsLayout) {
  try {
    window.localStorage.setItem(cacheKey(page), JSON.stringify(layout))
  } catch {
    // Storage is full or refused. The page is already rendering; nothing to do.
  }
}

export function CmsProvider({ page = 'home', children }: { page?: string; children: ReactNode }) {
  const [preview] = useState(isPreviewFrame)

  /* What the console is holding, unsaved. Null until the first message, so
     the frame shows the published page while the editor is still booting
     rather than flashing an empty one. */
  const [draft, setDraft] = useState<CmsLayout | null>(null)
  const [selected, setSelected] = useState<{ section: string | null; element: string | null }>({
    section: null,
    element: null,
  })

  /* The published page. Cached like the rest of the storefront: this changes
     a few times a month, and the live stream invalidates it when it does —
     see useCatalogueStream. In the frame it is only the first paint, so it is
     not refetched behind the draft. */
  const published = useQuery({
    queryKey: ['cms', page],
    queryFn: () => fetchLayout(page),
    staleTime: preview ? Infinity : 5 * 60_000,
    retry: 1,
    /* Warm, but never trusted: it seeds the first frame and is marked stale
       at once, so the request still goes out and the real answer replaces it.
       Skipped in the frame, where the console is about to send a draft. */
    initialData: preview ? undefined : () => readCache(page),
    initialDataUpdatedAt: 0,
  })

  const layout = published.data
  useEffect(() => {
    if (!preview && layout) writeCache(page, layout)
  }, [preview, page, layout])

  /* ---------- the wire ---------- */

  const post = useRef((message: CmsPreviewMessage) => {
    /* '*' rather than the console's origin: in development the two are
       different ports, in production the same host under /admin, and the
       payload is a section key — the same information the page is already
       showing. Nothing here is worth pinning an origin over, and pinning the
       wrong one silently breaks the preview. */
    window.parent.postMessage(message, '*')
  })

  useEffect(() => {
    if (!preview) return

    const onMessage = (event: MessageEvent) => {
      const data = event.data as CmsEditorMessage | undefined
      if (!data || data.source !== 'cms-editor') return

      if (data.type === 'layout') {
        setDraft(data.layout)
        return
      }

      if (data.type === 'highlight') {
        setSelected({ section: data.section, element: data.element })
        return
      }

      if (data.type === 'scroll') {
        const target = document.querySelector(`[data-cms-section="${CSS.escape(data.section)}"]`)
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    window.addEventListener('message', onMessage)
    post.current({ source: 'cms-preview', type: 'ready' })

    return () => window.removeEventListener('message', onMessage)
  }, [preview])

  /* Click-to-select. Captured before anything else on the page so a click
     lands on the editor rather than following the link it was on: inside the
     frame every anchor and button is a thing to be edited, not a thing to be
     pressed. */
  useEffect(() => {
    if (!preview) return

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const section = target?.closest<HTMLElement>('[data-cms-section]')
      if (!section) return

      event.preventDefault()
      event.stopPropagation()

      /* Three questions off one click, answered from the inside out: which
         piece of copy, which card, which band. The console opens the most
         specific one that came back, which is what makes clicking a button in
         the page put the cursor in that button's own box. */
      const field = target?.closest<HTMLElement>('[data-cms-field]')
      const element = target?.closest<HTMLElement>('[data-cms-el]')
      const key = section.dataset.cmsSection ?? ''

      setSelected({ section: key, element: element?.dataset.cmsEl ?? null })
      post.current({
        source: 'cms-preview',
        type: 'select',
        section: key,
        element: element?.dataset.cmsEl ?? null,
        field: field?.dataset.cmsField ?? null,
      })
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [preview])

  /* The class that turns on the outlines. On <html> rather than a wrapper so
     it reaches the page's own fixed furniture — the navbar, the CTA bar —
     which render outside this provider's subtree. */
  useEffect(() => {
    if (!preview) return
    document.documentElement.classList.add('cms-preview')
    return () => document.documentElement.classList.remove('cms-preview')
  }, [preview])

  /* Which section the reader is on, so the console's list can follow the
     scroll. Cheap: one observer over the eleven section wrappers. */
  useEffect(() => {
    if (!preview) return

    let current = ''
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const key = (entry.target as HTMLElement).dataset.cmsSection ?? ''
          if (key && key !== current) {
            current = key
            post.current({ source: 'cms-preview', type: 'inview', section: key })
          }
        }
      },
      { rootMargin: '-45% 0px -45% 0px' },
    )

    const attach = () => {
      observer.disconnect()
      document
        .querySelectorAll('[data-cms-section]')
        .forEach((node) => observer.observe(node))
    }

    attach()
    /* The draft changes which sections exist, and a section switched back on
       has to be observed. Re-attaching is a handful of nodes. */
    const timer = window.setTimeout(attach, 250)

    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
    }
  }, [preview, draft])

  const value = useMemo(
    () => ({ layout: draft ?? published.data ?? null, preview, selected }),
    [draft, published.data, preview, selected],
  )

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>
}
